import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Container,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TodayIcon from '@mui/icons-material/Today';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import useRole, { AppRole } from '@/personas/useRole';

// Bekräftelse går före snabbhet (Chiron AK8): visa den valda markeringen en
// kännbar stund innan appen mountas, så brukaren hinner se vad hen valde.
const CONFIRM_DELAY_MS = 300;

type RoleCardProps = {
  role: AppRole;
  title: string;
  description: string;
  icon: React.ReactNode;
  accentColor: string;
  accentBg: string;
  selected: boolean;
  dimmed: boolean;
  focusHint: boolean;
  focusHintLabel: string;
  autoFocus: boolean;
  disabled: boolean;
  onSelect: (role: AppRole) => void;
};

const RoleCard = ({
  role,
  title,
  description,
  icon,
  accentColor,
  accentBg,
  selected,
  dimmed,
  focusHint,
  focusHintLabel,
  autoFocus,
  disabled,
  onSelect,
}: RoleCardProps) => {
  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        flex: 1,
        borderRadius: 4,
        borderWidth: 2.5,
        borderColor: selected ? accentColor : 'divider',
        backgroundColor: selected ? accentBg : 'background.paper',
        opacity: dimmed ? 0.5 : 1,
        // Fokus-förval (Chiron AK3/4.3): ram + ring som signalerar "börja här",
        // ALDRIG ett valt/ikryssat utseende. Ingen rörelse av sig självt.
        ...(focusHint
          ? {
              borderColor: accentColor,
              boxShadow: `0 0 0 4px ${accentBg}`,
            }
          : {}),
        transition: 'border-color .15s, box-shadow .15s, background-color .15s, opacity .15s',
      }}
    >
      {focusHint && (
        <Chip
          label={focusHintLabel}
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: 16,
            zIndex: 1,
            backgroundColor: accentColor,
            color: 'common.white',
            fontWeight: 700,
            height: 22,
          }}
        />
      )}
      <CardActionArea
        autoFocus={autoFocus}
        disabled={disabled}
        aria-pressed={selected}
        onClick={() => onSelect(role)}
        sx={{
          minHeight: 160,
          height: '100%',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        {/* Markering med BÅDE färg OCH ikon (checkmark) — aldrig enbart färg. */}
        {selected && (
          <CheckCircleIcon sx={{ position: 'absolute', top: 14, right: 14, color: accentColor }} />
        )}
        <Stack direction="row" spacing={2.5} alignItems="center" width="100%">
          <Box
            aria-hidden
            sx={{
              width: 72,
              height: 72,
              borderRadius: 4,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: accentBg,
              color: accentColor,
              '& svg': { fontSize: 40 },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

/**
 * Rollväljarskärmen (v1). Visas av RoleGate när inget rollval persisterats för
 * användaren, samt vid rollbyte (clearRole). Två likvärdiga val; valet ÄR
 * bekräftelsen (ingen extra Fortsätt-knapp). Inget förvalt-markerat kort — kort A
 * har bara fokus-förval ("Börja här"). Ingen tidsgräns; en kort, lugn
 * bekräftelse-paus efter val.
 */
const RoleSelectScreen = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { setRole } = useRole();
  const [pending, setPending] = useState<AppRole | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rensa bekräftelse-timern vid avmontering så setRole inte fyrar på ett
  // avmonterat träd (t.ex. om RoleGate byter ut skärmen mitt i pausen).
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const userAccent = theme.palette.primary.main;
  const userBg = theme.palette.action.hover;
  // Handledaraccent (varm orange, samma ton som SupervisorHeader-läget).
  const supervisorAccent = theme.palette.warning.dark;
  const supervisorBg = theme.palette.warning.light;

  const handleSelect = (role: AppRole) => {
    if (pending) return; // hindra dubbelklick mitt i bekräftelse-pausen
    setPending(role);
    timeoutRef.current = setTimeout(() => {
      // setRole driver setSupervising → RoleGate släpper igenom children.
      setRole(role);
    }, CONFIRM_DELAY_MS);
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 6,
      }}
    >
      <Stack spacing={1} mb={4} textAlign="center">
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('How do you want to use the app today?')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('You can switch whenever you want.')}
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="stretch">
        <RoleCard
          role="user"
          title={t('My own day')}
          description={t('I see and do my own activities.')}
          icon={<TodayIcon />}
          accentColor={userAccent}
          accentBg={userBg}
          selected={pending === 'user'}
          dimmed={pending === 'supervisor'}
          // Fokus-förval på kort A (Chiron 4.3) — endast när inget val pågår.
          focusHint={pending === null}
          focusHintLabel={t('Start here')}
          autoFocus={pending === null}
          disabled={pending !== null}
          onSelect={handleSelect}
        />
        <RoleCard
          role="supervisor"
          title={t('Support someone')}
          description={t('I plan and support a person I help in everyday life.')}
          icon={<VolunteerActivismIcon />}
          accentColor={supervisorAccent}
          accentBg={supervisorBg}
          selected={pending === 'supervisor'}
          dimmed={pending === 'user'}
          focusHint={false}
          focusHintLabel=""
          autoFocus={false}
          disabled={pending !== null}
          onSelect={handleSelect}
        />
      </Stack>

      {/* Deklarativ insyns-text (v1 etiskt golv): ärlig mot faktiskt v1-beteende
          — handledaren ser brukarens dag och stjärnor, och brukaren får veta det. */}
      <Stack
        direction="row"
        spacing={1}
        mt={4}
        alignItems="flex-start"
        justifyContent="center"
        sx={{ color: 'text.secondary' }}
      >
        <InfoOutlinedIcon fontSize="small" sx={{ mt: '2px', flexShrink: 0 }} />
        <Typography variant="body2" sx={{ maxWidth: 460 }}>
          {t(
            'A person who supports you can see your day and your stars. You always know when someone is connected to you.',
          )}
        </Typography>
      </Stack>
    </Container>
  );
};

export default RoleSelectScreen;
